import { z } from 'zod';
import { BACKFILL_MIN_LAG_SECONDS, LIVE_DELIVERY_MAX_LAG_SECONDS } from '../tracking/index';

/**
 * Company settings the admin panel can read and write.
 *
 * Backed by the existing `settings` table (group/key/jsonb value), NOT by new
 * columns on `companies`. That table already carries RLS, a platform-default
 * row shape and an `is_public` flag for the mobile /config endpoint, so a new
 * setting is a row rather than a migration — which is the whole reason it was
 * built that way in docs/02-DATABASE-SCHEMA.md §5.
 */

/**
 * Which tiles the map draws.
 *
 * `osm` and `satellite` need no credentials and are always offered; everything
 * prefixed `google_` is dark until a browser key is saved, and the panel says
 * so rather than rendering an empty grey grid.
 */
export const BasemapId = z.enum([
  /** OpenStreetMap standard raster. The default, free, no key. */
  'osm',
  /** Esri World Imagery. Free satellite, no key, worldwide. */
  'satellite',
  'google_roadmap',
  'google_satellite',
  /** Satellite imagery WITH road and place labels — the one worth having. */
  'google_hybrid',
  'google_terrain',
]);
export type BasemapId = z.infer<typeof BasemapId>;

export const GOOGLE_BASEMAPS: readonly BasemapId[] = [
  'google_roadmap',
  'google_satellite',
  'google_hybrid',
  'google_terrain',
] as const;

export const isGoogleBasemap = (id: BasemapId): boolean =>
  (GOOGLE_BASEMAPS as readonly string[]).includes(id);

/**
 * The deepest zoom the map will go to.
 *
 * Above a provider's own maximum the tiles are upscaled rather than fetched, so
 * this buys magnification, not detail — which is exactly what is wanted when
 * several fixes land within a few metres of each other and the pins overlap.
 * OSM stops serving at 19 and Google's imagery at 20–21 depending on the area.
 */
export const MAX_SUPPORTED_ZOOM = 22;

export const MapSettingsSchema = z
  .object({
    /**
     * A Google Maps Platform **browser** key with the *Map Tiles API* enabled.
     *
     * Browser keys are not secrets — this one is sent to the browser by
     * definition, and Google's own guidance is to restrict it by HTTP referrer
     * rather than to hide it. It is still returned only to authenticated
     * admins, and the panel shows it masked until someone asks to see it.
     */
    googleMapsBrowserKey: z.string().max(200).nullable().default(null),
    /** Which basemap a fresh session starts on. */
    defaultBasemap: BasemapId.default('osm'),
    maxZoom: z.number().int().min(16).max(MAX_SUPPORTED_ZOOM).default(21),
    /**
     * Language and region for Google's labels. `region` biases disputed
     * borders and place naming; for an India deployment leaving it at 'IN'
     * matters, because the default renders boundaries the way a US viewer
     * expects and that is not a neutral choice.
     */
    labelLanguage: z.string().max(10).default('en'),
    labelRegion: z.string().max(4).default('IN'),
  })
  .strict();
export type MapSettings = z.infer<typeof MapSettingsSchema>;

export const DeliverySettingsSchema = z
  .object({
    /** At or under this lag (seconds) a fix counts as having been live. */
    liveLagSeconds: z.number().int().min(30).max(3600).default(LIVE_DELIVERY_MAX_LAG_SECONDS),
    /** At or over this lag a fix is a backfill. Must exceed liveLagSeconds. */
    backfillLagSeconds: z
      .number()
      .int()
      .min(60)
      .max(86_400)
      .default(BACKFILL_MIN_LAG_SECONDS),
  })
  .strict()
  .refine((v) => v.backfillLagSeconds > v.liveLagSeconds, {
    message: 'Backfill threshold must be longer than the live threshold',
    path: ['backfillLagSeconds'],
  });
export type DeliverySettings = z.infer<typeof DeliverySettingsSchema>;

export const CompanySettingsSchema = z.object({
  maps: MapSettingsSchema,
  delivery: DeliverySettingsSchema,
});
export type CompanySettings = z.infer<typeof CompanySettingsSchema>;

/**
 * A partial update. Every group is optional and every key within it is
 * optional, so the settings page can save one field without having to send —
 * and therefore without being able to clobber — the rest of the form.
 */
export const UpdateCompanySettingsSchema = z
  .object({
    maps: MapSettingsSchema.partial().optional(),
    delivery: DeliverySettingsSchema.innerType().partial().optional(),
  })
  .strict();
export type UpdateCompanySettings = z.infer<typeof UpdateCompanySettingsSchema>;

/** The defaults a company that has never saved anything gets. */
export const DEFAULT_COMPANY_SETTINGS: CompanySettings = {
  maps: MapSettingsSchema.parse({}),
  delivery: DeliverySettingsSchema.parse({}),
};
